import { mergeAttributes, Node, nodePasteRule } from "@tiptap/core"
import { getEmbedUrlFromVimeoUrl, isValidVimeoUrl, VIMEO_REGEX_GLOBAL } from "./vimeo-utils"

export interface VimeoOptions {
    addPasteHandler: boolean
    autoplay: boolean
    controls: boolean
    HTMLAttributes: Record<string, any>
    inline: boolean
    width: number
    height: number
}

type SetVimeoVideoOptions = { src: string; width?: number; height?: number }

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        vimeo: {
            setVimeoVideo: (options: SetVimeoVideoOptions) => ReturnType
        }
    }
}

export const Vimeo = Node.create<VimeoOptions>({
    name: "vimeo",

    addOptions() {
        return {
            addPasteHandler: true,
            autoplay: false,
            controls: true,
            HTMLAttributes: {},
            inline: false,
            width: 640,
            height: 360,
        }
    },

    inline() {
        return this.options.inline
    },

    group() {
        return this.options.inline ? "inline" : "block"
    },

    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
            },
            width: {
                default: this.options.width,
            },
            height: {
                default: this.options.height,
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: "div[data-vimeo-video] iframe",
            },
        ]
    },

    addCommands() {
        return {
            setVimeoVideo:
                (options: SetVimeoVideoOptions) =>
                ({ commands }) => {
                    if (!isValidVimeoUrl(options.src)) {
                        return false
                    }

                    return commands.insertContent([
                        {
                            type: "paragraph",
                            content: [],
                        },
                        {
                            type: this.name,
                            attrs: options,
                        },
                        {
                            type: "paragraph",
                            content: [],
                        },
                    ])
                },
        }
    },

    addPasteRules() {
        if (!this.options.addPasteHandler) {
            return []
        }

        return [
            nodePasteRule({
                find: VIMEO_REGEX_GLOBAL,
                type: this.type,
                getAttributes: (match) => {
                    return { src: match[0] }
                },
            }),
        ]
    },

    renderHTML({ HTMLAttributes }) {
        const embedUrl = getEmbedUrlFromVimeoUrl({
            url: HTMLAttributes.src,
            autoplay: this.options.autoplay,
            controls: this.options.controls,
        })

        if (!embedUrl) {
            return ["span", { "data-vimeo-video": "", "data-invalid": "true", style: "display: none;" }]
        }

        const { src, ...rest } = HTMLAttributes

        return [
            "div",
            { "data-vimeo-video": "" },
            [
                "iframe",
                mergeAttributes(
                    this.options.HTMLAttributes,
                    {
                        width: this.options.width,
                        height: this.options.height,
                        src: embedUrl,
                        frameborder: "0",
                        allow: "autoplay; fullscreen; picture-in-picture",
                        allowfullscreen: "true",
                    },
                    rest
                ),
            ],
        ]
    },
})
